(function () {
  // sections/latest-videos.liquid reuses the <latest-updates-carousel> custom
  // element/markup from sections/latest-updates.liquid for identical prev/next
  // scroll behavior and box sizing. That element's class is normally defined by
  // latest-updates.liquid's own inline script - but this page may not include
  // that section at all (e.g. it's been swapped out for just the video
  // carousel), so the class needs to be defined here too. Guarded by
  // customElements.get so it's a no-op if latest-updates.liquid's script also
  // ran on this page.
  if (!customElements.get('latest-updates-carousel')) {
    class LatestUpdatesCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-latest-updates-track]');
        this.prevButton = this.querySelector('[data-latest-updates-prev]');
        this.nextButton = this.querySelector('[data-latest-updates-next]');
        if (!this.track) return;

        if (this.prevButton) this.prevButton.addEventListener('click', () => this.scrollByAmount(-1));
        if (this.nextButton) this.nextButton.addEventListener('click', () => this.scrollByAmount(1));
        this.track.addEventListener('scroll', () => this.updateButtonStates());
        this.updateButtonStates();
      }

      scrollByAmount(direction) {
        // Jump by a full visible-track-width "page" rather than the old
        // 80%-of-width partial scroll, so Next/Prev reveals a clean next/
        // previous batch of cards (e.g. videos 7-12 after one click with
        // 12 total loaded and ~6 visible at a time) instead of a
        // continuously-overlapping scroll.
        const scrollAmount = this.track.offsetWidth * direction;
        this.track.scrollLeft += scrollAmount;
      }

      updateButtonStates() {
        if (!this.prevButton || !this.nextButton) return;
        const isAtStart = this.track.scrollLeft <= 0;
        const isAtEnd = this.track.scrollLeft + this.track.offsetWidth >= this.track.scrollWidth - 1;
        this.prevButton.disabled = isAtStart;
        this.nextButton.disabled = isAtEnd;
      }
    }

    customElements.define('latest-updates-carousel', LatestUpdatesCarousel);
  }

  var CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour - keeps repeat page views within a session from re-hitting the API quota.

  function cacheKey(handle) {
    return 'latest-videos:v3:' + handle;
  }

  function readCache(handle) {
    try {
      var raw = sessionStorage.getItem(cacheKey(handle));
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.timestamp || Date.now() - parsed.timestamp > CACHE_TTL_MS) return null;
      return parsed.videos;
    } catch (e) {
      return null;
    }
  }

  function writeCache(handle, videos) {
    try {
      sessionStorage.setItem(cacheKey(handle), JSON.stringify({ timestamp: Date.now(), videos: videos }));
    } catch (e) {
      // Storage full/unavailable - not fatal, just skip caching.
    }
  }

  function escapeHtml(value) {
    // Used inside src="..."/alt="..." attribute values (video title,
    // thumbnail URL) built from the YouTube API response, not just text
    // nodes - a plain textContent round-trip escapes &/</> but not "/',
    // which isn't enough for attribute context. Escape all five explicitly.
    return (value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatDate(iso) {
    try {
      return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (e) {
      return '';
    }
  }

  function videoCardHtml(video) {
    var title = escapeHtml(video.title);
    var thumb = video.thumbnail;
    var url = 'https://www.youtube.com/watch?v=' + encodeURIComponent(video.videoId);
    return (
      '<a href="' + url + '" target="_blank" rel="noopener" class="latest-updates__card">' +
        '<div class="latest-updates__image-wrapper">' +
          '<img src="' + escapeHtml(thumb) + '" alt="' + title + '" loading="lazy" class="latest-updates__image">' +
          '<div class="latest-updates__image-overlay">' +
            '<p class="latest-updates__overlay-title">' + title + '</p>' +
          '</div>' +
        '</div>' +
        '<div class="latest-updates__content">' +
          '<div class="latest-updates__meta">' +
            '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
              '<rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>' +
              '<line x1="16" y1="2" x2="16" y2="6"></line>' +
              '<line x1="8" y1="2" x2="8" y2="6"></line>' +
              '<line x1="3" y1="10" x2="21" y2="10"></line>' +
            '</svg>' +
            '<span>' + escapeHtml(formatDate(video.publishedAt)) + '</span>' +
          '</div>' +
          '<h3 class="latest-updates__card-title">' + title + '</h3>' +
          '<span class="latest-updates__read-more">' +
            'Watch now' +
            '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
              '<line x1="5" y1="12" x2="19" y2="12"></line>' +
              '<polyline points="12 5 19 12 12 19"></polyline>' +
            '</svg>' +
          '</span>' +
        '</div>' +
      '</a>'
    );
  }

  function renderVideos(track, videos, el) {
    if (!videos || videos.length === 0) {
      track.innerHTML = '<p class="latest-updates__empty">No videos found.</p>';
      return;
    }
    track.innerHTML = videos.map(videoCardHtml).join('');
    // The carousel's prev/next disabled state is computed once when the
    // element connects to the DOM - before this async render fills the
    // track with content that actually overflows. Recompute it now so
    // "Next" isn't stuck disabled from that first (empty-track) check.
    if (el && typeof el.updateButtonStates === 'function') {
      el.updateButtonStates();
    }
  }

  function renderError(track) {
    track.innerHTML = '<p class="latest-updates__empty">Unable to load videos right now.</p>';
  }

  function fetchUploadsPlaylistId(handle, apiKey) {
    var url =
      'https://www.googleapis.com/youtube/v3/channels?part=contentDetails&forHandle=' +
      encodeURIComponent(handle) +
      '&key=' +
      encodeURIComponent(apiKey);
    return fetch(url)
      .then(function (res) {
        if (!res.ok) throw new Error('channels.list failed');
        return res.json();
      })
      .then(function (data) {
        var item = data.items && data.items[0];
        var uploads = item && item.contentDetails && item.contentDetails.relatedPlaylists && item.contentDetails.relatedPlaylists.uploads;
        if (!uploads) throw new Error('No uploads playlist found for channel handle');
        return uploads;
      });
  }

  function fetchPlaylistVideos(playlistId, apiKey, maxResults) {
    var url =
      'https://www.googleapis.com/youtube/v3/playlistItems?part=snippet&playlistId=' +
      encodeURIComponent(playlistId) +
      '&maxResults=' +
      encodeURIComponent(maxResults) +
      '&key=' +
      encodeURIComponent(apiKey);
    return fetch(url)
      .then(function (res) {
        if (!res.ok) throw new Error('playlistItems.list failed');
        return res.json();
      })
      .then(function (data) {
        return (data.items || [])
          .filter(function (item) {
            return item.snippet && item.snippet.resourceId && item.snippet.resourceId.videoId;
          })
          .map(function (item) {
            var snippet = item.snippet;
            var thumbnails = snippet.thumbnails || {};
            var thumbnail =
              (thumbnails.maxres && thumbnails.maxres.url) ||
              (thumbnails.standard && thumbnails.standard.url) ||
              (thumbnails.high && thumbnails.high.url) ||
              (thumbnails.medium && thumbnails.medium.url) ||
              (thumbnails.default && thumbnails.default.url) ||
              '';
            return {
              videoId: snippet.resourceId.videoId,
              title: snippet.title,
              thumbnail: thumbnail,
              publishedAt: snippet.publishedAt
            };
          });
      });
  }

  function init(el) {
    var track = el.querySelector('[data-latest-videos-track]');
    if (!track) return;

    var handle = el.getAttribute('data-channel-handle');
    var apiKey = el.getAttribute('data-api-key');
    var maxResults = parseInt(el.getAttribute('data-max-videos'), 10) || 6;
    if (!handle || !apiKey) return;

    var cached = readCache(handle);
    if (cached) {
      renderVideos(track, cached, el);
      return;
    }

    fetchUploadsPlaylistId(handle, apiKey)
      .then(function (uploadsPlaylistId) {
        return fetchPlaylistVideos(uploadsPlaylistId, apiKey, maxResults);
      })
      .then(function (videos) {
        writeCache(handle, videos);
        renderVideos(track, videos, el);
      })
      .catch(function () {
        renderError(track);
      });
  }

  function initAll() {
    document.querySelectorAll('[data-latest-videos]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
